/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Address, AddressPurpose, CountryRegionInfo, StateProvinceInfo } from '@msdyn365-commerce/retail-proxy';
import { ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { getTelemetryObject, IModuleProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { observable, reaction, set } from 'mobx';
import * as React from 'react';

import { AddressCommon } from '@msdyn365-commerce-modules/address';
import { AddressFormat } from '@msdyn365-commerce-modules/address';
import { AddressItemDisplayType, AddressItemType, AddressValidationRuleType, IAddressItem } from '@msdyn365-commerce-modules/address';
import { AddressMetaData } from '@msdyn365-commerce-modules/address';
import { AddressOperation, AddressType, IAddressResource, IAddressResponse } from '@msdyn365-commerce-modules/address';
import { AddressAddUpdate, IAddressAddUpdateProps } from '@msdyn365-commerce-modules/address';
import { ICelliBusinessAccountAddressData } from './celli-business-account-address.data';
import { ICelliBusinessAccountAddressProps } from './celli-business-account-address.props.autogenerated';

/**
 * Interface for IBusinessAccountAddressExtendedProps.
 */
export interface IBusinessAccountAddressExtendedProps extends ICelliBusinessAccountAddressProps<ICelliBusinessAccountAddressData> {
    hasError: boolean;
    isRequired: boolean;
    resetAddress: boolean;
    updateForm(errors: boolean, currentAddress?: Address): void;
}

/**
 * Interface for IBusinessAccountAddressState.
 */
export interface IBusinessAccountAddressState {
    countryId?: string;
    states?: StateProvinceInfo[];
}

/**
 * Interface for IBusinessAccountAddressViewProps.
 */
export interface IBusinessAccountAddressViewProps {
    className: string;
    currentOperation: AddressOperation;
    selectedAddress?: Address;
    addUpdateAddress: Address;
    addressListSelectedAddress: Address;
    countryRegionId: string;
    stateProvinceInfo?: StateProvinceInfo[];
    customerAddresses: Address[];
    validationError: object;
    addressActionResponse?: IAddressResponse;
    BusinessAccountAddress: IModuleProps;
    isUpdating: boolean;
    hasError: boolean;
    showAddOrUpdateAddress: IAddressAddUpdateProps;
}

/**
 *
 * Address component.
 * @extends {React.Component<ICelliBusinessAccountAddressProps<ICelliBusinessAccountAddressData>, IBusinessAccountAddressState>}
 */
class BusinessAccountAddress extends React.Component<IBusinessAccountAddressExtendedProps, IBusinessAccountAddressState> {
    @observable private addUpdateAddress: Address;

    @observable private countryRegionId: string = 'USA';

    @observable private stateProvinceInfo?: StateProvinceInfo[];

    @observable private readonly validationError: object;

    private readonly addressActionResponse?: IAddressResponse;

    private readonly isUpdating?: boolean;

    private readonly addressCommon: AddressCommon;

    private addressFormat: AddressFormat;

    private countryRegions: CountryRegionInfo[] = [];

    private addressPurposes: AddressPurpose[] = [];

    private readonly resources: IAddressResource;

    private readonly defaultAddressType: number = 0; // Default to None (This value set in HQ as Business)

    private readonly shouldAutoFocus?: boolean = false;

    private readonly excludedList: AddressItemType[] = [AddressItemType.Name, AddressItemType.AddressTypeValue, AddressItemType.IsPrimary]; // Default to hiding Name and AddressType inputs

    private readonly telemetryContent: ITelemetryContent;

    private autocomplete?: any;

    public constructor(props: IBusinessAccountAddressExtendedProps) {
        super(props);

        const { context, data, resources, telemetry } = this.props;
        this.addUpdateAddress = {};
        this.resources = resources;
        this.countryRegions = data.countryRegions.result || [];
        this.addressPurposes = data.addressPurposes.result || [];
        this.stateProvinceInfo = data.countryStates.result || [];
        this.addressFormat = new AddressFormat(
            this.countryRegions,
            new AddressMetaData({ ...resources }, this.excludedList, props.isRequired ? undefined : []),
            this.addressPurposes
        );
        this.addressCommon = new AddressCommon(context, resources, telemetry);
        this.validationError = {};
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
    }

    public componentDidMount(): void {
        // Initializing data props
        this._dataInitialize(this.props);

        this._setDefaultCountryRegionId();

        this.loadGoogleMaps();
    }

    private loadGoogleMaps = () => {
        const script = document.createElement('script');
        script.src = 'https://maps.googleapis.com/maps/api/js?key=AIzaSyD23CXnyKH4t0bX1AZe3e0V8lVQHfRppOs&libraries=places';
        script.async = true;
        script.defer = true;
        script.onload = this.initializeAutocomplete;
        document.head.appendChild(script);
    };

    private handlePlaceChanged = () => {
        if (!this.autocomplete) {
            return;
        }

        const place = this.autocomplete.getPlace();
        if (!place || !place.address_components) {
            console.error('No place details available.');
            return;
        }

        let city = '';
        let country = '';
        let state = '';
        let county = '';
        let zipcode = '';
        let street = '';

        for (const component of place.address_components) {
            if (component.types.includes('locality') || component.types.includes('administrative_area_level_3')) {
                city = component.long_name;
            } else if (component.types.includes('administrative_area_level_2')) {
                county = component.short_name;
            } else if (component.types.includes('postal_code')) {
                zipcode = component.long_name;
            } else if (component.types.includes('country')) {
                country = component.long_name;
            } else if (component.types.includes('administrative_area_level_1')) {
                state = component.long_name;

                if (state && this.stateProvinceInfo?.length) {
                    // @ts-ignore
                    const matchedState = this.stateProvinceInfo.find(
                        s =>
                            // @ts-ignore
                            s.StateName.trim()
                                .toLowerCase()
                                .replace(/[\s-]/g, '') ===
                            state
                                .trim()
                                .toLowerCase()
                                .replace(/[\s-]/g, '')
                    );

                    if (matchedState) {
                        // @ts-ignore
                        this._stateManuallySet = true;
                        // @ts-ignore
                        this._updateAddress('State', matchedState.StateId);

                        const stateSelect = document.getElementById('company_addressstate') as HTMLSelectElement;
                        if (stateSelect && stateSelect.value !== matchedState.StateId) {
                            // @ts-ignore
                            stateSelect.value = matchedState.StateId;
                            // stateSelect.dispatchEvent(new Event('change', { bubbles: true }));
                            // stateSelect.dispatchEvent(new Event('input', { bubbles: true }));
                        }
                    }
                }
            }
        }

        const streetNumberComponent = place.address_components.find((component: any) => component.types.includes('street_number'));
        const routeComponent = place.address_components.find((component: any) => component.types.includes('route'));
        street =
            routeComponent && streetNumberComponent
                ? `${routeComponent.long_name}, ${streetNumberComponent.long_name}`
                : place.formatted_address;

        const streetInput = document.getElementById('company_addressstreet') as HTMLInputElement;
        if (streetInput) {
            streetInput.value = street;
            streetInput.setAttribute('region', state);
        }

        const cityInput = document.getElementById('company_addresscity') as HTMLInputElement;
        if (cityInput) {
            cityInput.value = city;
        }

        const stateInput = document.getElementById('company_addressstate') as HTMLInputElement;
        if (stateInput) {
            //stateInput.value = state;
        }

        const countyInput = document.getElementById('company_addresscounty') as HTMLInputElement;
        if (countyInput) {
            countyInput.value = county;
        }

        const zipcodeInput = document.getElementById('company_addresszipcode') as HTMLInputElement;
        if (zipcodeInput) {
            zipcodeInput.value = zipcode;
        }

        const countrySelect = document.getElementById('company_addressthreeletterisoregionname') as HTMLSelectElement;
        if (countrySelect) {
            let countryValue = '';
            // @ts-ignore
            for (const option of countrySelect.options) {
                if (option.textContent?.trim().toLowerCase() === country.trim().toLowerCase()) {
                    countryValue = option.value;
                    break;
                }
            }

            if (countryValue) {
                countrySelect.value = countryValue;
                countrySelect.dispatchEvent(new Event('change', { bubbles: true }));
                countrySelect.dispatchEvent(new Event('input', { bubbles: true }));
            }
        }

        const stateSelect = document.getElementById('company_addressstate') as HTMLSelectElement;
        if (stateSelect) {
            let stateValue = '';
            // @ts-ignore
            for (const option of stateSelect.options) {
                if (option.textContent?.trim().toLowerCase() === state.trim().toLowerCase()) {
                    stateValue = option.value;
                    break;
                }
            }

            if (stateValue) {
                stateSelect.value = stateValue;
            }
        }

        this.addUpdateAddress.Street = street;
        this.addUpdateAddress.County = county;
        this.addUpdateAddress.City = city;
        this.addUpdateAddress.ZipCode = zipcode;
    };

    private initializeAutocomplete = () => {
        const inputElement = document.getElementById('company_addressstreet') as HTMLInputElement;

        // @ts-ignore
        if (window.google?.maps) {
            // @ts-ignore
            this.autocomplete = new window.google.maps.places.Autocomplete(inputElement);
            this.autocomplete.setFields(['address_components', 'formatted_address', 'geometry', 'place_id']);

            // @ts-ignore
            window.google.maps.event.addListener(this.autocomplete, 'place_changed', this.handlePlaceChanged);
        } else {
            console.error('Google Maps API is not available.');
        }
    };

    public shouldComponentUpdate(nextProps: IBusinessAccountAddressExtendedProps, nextState: IBusinessAccountAddressState): boolean {
        // Ignore TypeScript error on this line
        //@ts-ignore
        return !(this.state === nextState && this.props.data === nextProps.data);
    }

    public render(): JSX.Element | null {
        const { renderView, config, hasError, resetAddress } = this.props;

        // Business-sign-up will pass this flag when cancel button is clicked
        if (resetAddress) {
            this._resetAddressFields();
        }

        const viewProps = {
            ...this.props,
            countryRegionId: this.countryRegionId,
            stateProvinceInfo: this.stateProvinceInfo,
            validationError: this.validationError,
            addressActionResponse: this.addressActionResponse,
            hasError,
            className: config.className,
            BusinessAccountAddress: {
                moduleProps: this.props,
                className: classnames('ms-business-account-address', config.className)
            },
            showAddOrUpdateAddress: this._renderAddOrUpdateAddress()
        };

        return renderView(viewProps) as React.ReactElement;
    }

    /**
     * Reset Address.
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention
    public _resetAddressFields = () => {
        const addressFormat = this.addressFormat.getAddressFormat(this.countryRegionId);

        addressFormat.map(addressFormatItem => {
            switch (addressFormatItem.displayType) {
                case AddressItemDisplayType.Input:
                    this.addUpdateAddress[addressFormatItem.name] = '';
                    break;
                case AddressItemDisplayType.Checkbox:
                    this.addUpdateAddress[addressFormatItem.name] = false;
                    break;
                default:
                    switch (addressFormatItem.type) {
                        case AddressItemType.ThreeLetterISORegionName:
                            this.addUpdateAddress[addressFormatItem.name] = this.countryRegionId;
                            break;
                        case AddressItemType.AddressTypeValue:
                            this.addUpdateAddress[addressFormatItem.name] = this.defaultAddressType;
                            break;
                        case AddressItemType.State:
                            this.addUpdateAddress[addressFormatItem.name] = '';
                            break;
                        default:
                            this.addUpdateAddress[addressFormatItem.name] = undefined;
                    }
            }
        });
    };

    /**
     * Method data initialization.
     * @param props -The account management props.
     */
    private readonly _dataInitialize = (props: IBusinessAccountAddressExtendedProps): void => {
        const { data } = props;

        reaction(
            () => data.countryRegions.result,
            () => {
                this.countryRegions = data.countryRegions.result ?? [];
            }
        );

        reaction(
            () => data.addressPurposes.result,
            () => {
                this.addressPurposes = data.addressPurposes.result ?? [];
            }
        );

        reaction(
            () => data.countryStates.result,
            () => {
                this.stateProvinceInfo = data.countryStates.result ?? [];
            }
        );
    };

    // Set validationError and hasError to hide error messages since in the B2B
    // request form, no errors would visually show since user wont be able to submit
    // until form is filled and correct
    /**
     * Render Add Or Update Address Function.
     * @returns - AddressAddUpdate.
     */
    private readonly _renderAddOrUpdateAddress = (): IAddressAddUpdateProps => {
        const addressFormat = this.addressFormat.getAddressFormat(this.countryRegionId);
        // Add * to required fields that don't have it
        addressFormat.forEach(item => {
            if ((item.name === 'County' || item.name === 'Phone') && !item.label.includes('*')) {
                item.label += ' *';
            }
        });

        return AddressAddUpdate({
            isUpdating: this.isUpdating,
            resources: this.resources,
            addressType: AddressType.Company,
            addressFormat: addressFormat,
            defaultCountryRegionId: this.countryRegionId,
            defaultAddressType: this.defaultAddressType,
            selectedAddress: this.addUpdateAddress,
            shouldAutoFocus: this.shouldAutoFocus,
            validationError: this.validationError,
            hasError: this.props.hasError,
            addressActionResponse: this.addressActionResponse,
            telemetryContent: this.telemetryContent,
            dropdownDisplayData: this.addressFormat.getPrefilledAddressDropdownData(
                this.resources.addressStateDefaultSelectionText,
                this.stateProvinceInfo
            ),
            onInputChange: this._onAddressAddUpdateInputChange,
            onDropdownChange: this._onAddressAddUpdateDropdownChange
        });
    };

    /**
     * On Address Add Update Input Change Function.
     * @param event - Input event change function.
     */
    private readonly _onAddressAddUpdateInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        this._updateAddress(event.target.name, event.target.value);
    };

    /**
     * On Address Add Update Dropdown Change Function.
     * @param event - Change dropdown event.
     */
    private readonly _onAddressAddUpdateDropdownChange = async (event: React.ChangeEvent<HTMLSelectElement>): Promise<void> => {
        if (event.target.name === AddressItemType[AddressItemType.ThreeLetterISORegionName]) {
            await this._updateCountryRegionId(event.target.value);
        } else {
            this._updateAddress(event.target.name, event.target.value);
        }
    };

    /**
     * Update Address Function.
     * @param name - String.
     * @param value - String.
     */
    private readonly _updateAddress = (name: string, value: string | number) => {
        const { updateForm, isRequired, resources } = this.props;
        let cleanValue = value;
        if (typeof value === 'string') {
            cleanValue = (value || '').replace(new RegExp('[<>]', 'gi'), '');
        }
        set(this.addUpdateAddress, { [name]: cleanValue });

        if (this._isEmpty()) {
            if (isRequired) {
                updateForm(true, undefined);
            } else {
                // Reset addressFormat as if address was NOT a required field
                this.addressFormat = new AddressFormat(
                    this.countryRegions,
                    new AddressMetaData({ ...resources }, this.excludedList, []),
                    this.addressPurposes
                );
                updateForm(false, this.addUpdateAddress);
            }
        } else {
            // Get new addressFormat if address was a required field
            this.addressFormat = new AddressFormat(
                this.countryRegions,
                new AddressMetaData({ ...resources }, this.excludedList, this.props.isRequired ? undefined : []),
                this.addressPurposes
            );

            const format = this.addressFormat.getAddressFormat(this.countryRegionId);
            // Clear previous errors
            Object.keys(this.validationError).forEach(key => delete this.validationError[key]);

            // Check required fields
            for (const item of format) {
                const value = this.addUpdateAddress[item.name];
                if (!value || value === '') {
                    (this.validationError as any)[item.name] = 'Required';
                }
            }

            if (Object.keys(this.validationError).length > 0) {
                updateForm(true, undefined);
            } else {
                updateForm(false, this.addUpdateAddress);
            }
        }
    };

    /**
     * Is empty Function.
     * @returns - Boolean.
     * Walk through array and check if valued is defined or value is not an empty string
     */
    private readonly _isEmpty = () => {
        const addressFormat = this.addressFormat.getAddressFormat(this.countryRegionId);
        for (const addressFormatItem of addressFormat) {
            if (addressFormatItem.type !== AddressItemType.ThreeLetterISORegionName || this._isInputRequired(addressFormatItem)) {
                const addressItemValue = this.addUpdateAddress[addressFormatItem.name];

                // Check each input field, if value is defined and not an empty string --> address is not empty
                if (addressItemValue || (addressItemValue && addressItemValue !== '')) {
                    return false;
                }
            }
        }

        return true;
    };

    /**
     * Is Input Required Function.
     * @param addressFormatItem - IAddressItem.
     * @returns - Boolean.
     */
    private readonly _isInputRequired = (addressFormatItem: IAddressItem): boolean => {
        if (
            ObjectExtensions.isNullOrUndefined(addressFormatItem) ||
            !addressFormatItem.validationRules ||
            addressFormatItem.validationRules.length === 0
        ) {
            return false;
        }

        for (const validationRule of addressFormatItem.validationRules) {
            if (validationRule.type === AddressValidationRuleType.Required) {
                return false;
            }
        }

        return true;
    };

    private _setDefaultCountryRegionId(): void {
        const { request } = this.props.context;
        const market = request.channel?.ChannelCountryRegionISOCode;

        void this._updateCountryRegionId(this.addressCommon.getDefaultCountryRegionId(this.countryRegionId, this.countryRegions, market));

        // Set default AddressType
        void set(this.addUpdateAddress, { AddressTypeValue: this.defaultAddressType });
    }

    /**
     * Update Country Region Id Function.
     * @param value - String.
     */
    private readonly _updateCountryRegionId = async (value: string) => {
        this.countryRegionId = value;

        await this.addressCommon.getStateProvinces(this.countryRegionId).then((result: StateProvinceInfo[]) => {
            const stateInfo = result.some(state => state.StateId === this.addUpdateAddress.State);

            // @ts-ignore
            if (!stateInfo && !this._stateManuallySet) {
                this._updateAddress('State', '');
            }

            this.stateProvinceInfo = result;

            // MOCK ONLY - Local testing workaround
            // Special case for Italy since Italy states are not coming from HQ
            if (
                (this.countryRegionId === 'ITA' || this.countryRegionId.toLowerCase() === 'italy') &&
                (!this.stateProvinceInfo || this.stateProvinceInfo.length === 0)
            ) {
                const italianStates: StateProvinceInfo[] = [
                    { StateId: 'ABR', StateName: 'ABRUZZO' },
                    { StateId: 'BAS', StateName: 'BASILICATA' },
                    { StateId: 'CAL', StateName: 'CALABRIA' },
                    { StateId: 'CAM', StateName: 'CAMPANIA' },
                    { StateId: 'EMI', StateName: 'EMILIA ROMAGNA' },
                    { StateId: 'EMR', StateName: 'DO NOT USE' },
                    { StateId: 'FRI', StateName: 'FRIULI VENEZIA GIULIA' },
                    { StateId: 'LAZ', StateName: 'LAZIO' },
                    { StateId: 'LIG', StateName: 'LIGURIA' },
                    { StateId: 'LOM', StateName: 'LOMBARDIA' },
                    { StateId: 'MAR', StateName: 'MARCHE' },
                    { StateId: 'MOL', StateName: 'MOLISE' },
                    { StateId: 'ND', StateName: 'NON UTILIZZARE' },
                    { StateId: 'PIE', StateName: 'PIEMONTE' },
                    { StateId: 'PUG', StateName: 'PUGLIA' },
                    { StateId: 'SAR', StateName: 'SARDEGNA' },
                    { StateId: 'SIC', StateName: 'SICILIA' },
                    { StateId: 'TOS', StateName: 'TOSCANA' },
                    { StateId: 'TRE', StateName: 'TRENTINO ALTO ADIGE' },
                    { StateId: 'UMB', StateName: 'UMBRIA' },
                    { StateId: 'VAL', StateName: 'VALLE D AOSTA' },
                    { StateId: 'VEN', StateName: 'VENETO' }
                ];
                this.stateProvinceInfo = italianStates;
            }
        });

        this._updateAddress('ThreeLetterISORegionName', this.countryRegionId);

        const streetInput = document.getElementById('company_addressstreet') as HTMLInputElement;
        const stateSelect = document.getElementById('company_addressstate') as HTMLSelectElement;

        if (streetInput && streetInput.hasAttribute('region') && stateSelect) {
            const regionFromStreet = streetInput.getAttribute('region')!;
            const matchedOption = Array.from(stateSelect.options).find(
                option => option.textContent?.trim().toLowerCase() === regionFromStreet.trim().toLowerCase()
            );

            if (matchedOption) {
                stateSelect.value = matchedOption.value;
                stateSelect.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }
    };
}

export default BusinessAccountAddress;
